import React, { useEffect, useState, useCallback, useRef } from "react"
import { connect } from "react-redux"
import { updateCart } from "../../../../v1/actions/user"
import { useTranslation } from "react-i18next"
import { getUpdatedCart } from "../../../../v1/helpers/orderHelpers"
import { Button, Card, Divider, Icon, Input } from "semantic-ui-react"
import { ShimmerServiceCard } from "../../Services/ShimmerServiceCard"
import ServiceCard from "./ServiceCard"
import {
  gaJSON,
  trackFirebaseAnalytics
} from "../../../../shared_component/utils/googleAnalyticsHelpers"
import { postJSON } from "../../../../shared_component/utils/fetch"
import debounce from "lodash/debounce"
import { routerPaths } from "../../../../v1/constants/paths"
import { toggleService } from "./SelectService"
import { showChatWidget } from "../../../../v1/helpers/userHelper"

const SearchServices = (props) => {
  const [services, setServices] = useState([])
  const [selectedPositions, setSelectedPositions] = useState({})
  const [serviceErrors, setServiceErrors] = useState({})
  const [selectedServices, setSelectedServices] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const inputRef = useRef(null)
  const [servicesUpdating, setServicesUpdating] = useState([])

  const { cart, history } = props
  const { t } = useTranslation("addService")

  useEffect(() => {
    inputRef.current?.focus()
    const allServiceIds = cart.order_services.map((service) => service.id)
    setSelectedServices(allServiceIds)
  }, [])

  const fetchCategoriesServices = useCallback(async (searchQuery = "") => {
    const SEARCH_SERVICES_URL = "/api/v4/services/search"
    const { user, cart } = props

    if (searchQuery.length <= 1) {
      setServices([])
      setIsLoading(false)
      return
    }

    setIsLoading(true)
    const response = await postJSON(
      SEARCH_SERVICES_URL,
      {
        query: {
          type: "terms",
          where: searchQuery
        },
        vehicle_id: user.activeVehicleId
      },
      {
        Authorization: user.authentication_token
      }
    )

    const categoryServices = response?.result || []

    if (categoryServices !== null) {
      setServices(categoryServices)

      const selectedPositionsMap = cart.order_services.reduce((acc, service) => {
        if (service.position && service.position != "") {
          acc[service.id] = service.position
        }
        return acc
      }, {})
      categoryServices.forEach((service) => {
        if ((service.positions?.length || 0) > 0) {
          selectedPositionsMap[service.id] = service.positions[0]
        }
      })
      setSelectedPositions(selectedPositionsMap)
      trackFirebaseAnalytics({
        event: gaJSON.service_search_used,
        params: {
          search_term: searchQuery
        }
      })
    }
    setIsLoading(false)
  }, [])

  const debouncedFetch = useCallback(debounce(fetchCategoriesServices, 500), [
    fetchCategoriesServices
  ])

  const handleSearchChange = (e) => debouncedFetch(e.target.value)

  const updateService = async () => {
    history.push({
      pathname: routerPaths.reviewServices
    })
  }

  const handleService = async (service, position = null) => {
    toggleService({
      service,
      positionOverride: position,
      cart: props.cart,
      order: props.order,
      vehicle: props.vehicle,
      selectedPositions,
      setSelectedPositions,
      setServiceErrors,
      setServicesUpdating,
      setSelectedServices,
      t,
      updateCart: props.updateCart
    })
  }

  const updateServiceWithPosition = async (service, position = "") => {
    setServiceErrors((prev) => {
      const { [service.id]: removed, ...rest } = prev
      return rest
    })

    setSelectedPositions((prev) => ({
      ...prev,
      [service.id]: position
    }))
    handleService(service, true, false)
  }

  const getPositions = (positions) => {
    return positions.map((position, index) => ({
      key: index,
      text: position,
      value: position
    }))
  }

  return (
    <Card className="service-card" raised={true}>
      <Input iconPosition="left" placeholder="Search">
        <Icon name="search" />
        <input ref={inputRef} onChange={handleSearchChange} />
        {inputRef?.current?.value && (
          <button
            onClick={() => {
              inputRef.current.value = ""
              setServices([])
            }}
            style={{
              position: "absolute",
              right: "10px",
              top: "25px",
              transform: "translateY(-50%)",
              background: "none",
              border: "none",
              cursor: "pointer",
              fontSize: "1em",
              color: "#666"
            }}
            aria-label="Clear input"
          >
            ✕
          </button>
        )}
      </Input>
      {isLoading && <ShimmerServiceCard count={5} />}
      {!isLoading && (inputRef.current?.value?.length || 0) > 1 && (
        <>
          <p className="showing-results">
            <Icon name="search" /> showing results for: "{inputRef.current?.value}"
          </p>
        </>
      )}
      {!isLoading &&
        services &&
        services.map((service, index) => (
          <React.Fragment key={service.id}>
            <ServiceCard
              service={service}
              selectedServices={selectedServices}
              isUpdating={servicesUpdating.includes(service.id)}
              selectedPositions={selectedPositions}
              serviceErrors={serviceErrors}
              handleService={handleService}
              updateServiceWithPosition={handleService}
              getPositions={getPositions}
            />
            {index !== services.length - 1 && <Divider />}
          </React.Fragment>
        ))}
      {!isLoading && services.length == 0 && (inputRef.current?.value?.length || 0) > 1 && (
        <div>
          <p>No results? Try checking your spelling or searching for a more general term.</p>
          <p>
            Still can't find it? <span> </span>
            <span className="link">
              <a href="tel:+1-844-923-8473"> {t("Contact us")} </a>
            </span>
          </p>
        </div>
      )}
      {selectedServices.length > 0 && (
        <div className="action_button">
          <Button
            onClick={updateService}
            disabled={!props?.cart?.order_services || props.cart.order_services.length <= 0}
            className="primary-button"
          >
            {t("Continue")}
          </Button>
        </div>
      )}
    </Card>
  )
}

function mapStateToProps(state, props) {
  let user = state.user || {}
  let cart
  let activeVehicle = user.vehicles && user.vehicles.find((v) => v.id == user.activeVehicleId)

  const order =
    activeVehicle &&
    activeVehicle.id &&
    (user.active_orders || []).find((o) => o.vehicle_id === activeVehicle.id)
  let updatedCartData = getUpdatedCart(user, order, props)
  if (updatedCartData) {
    cart = updatedCartData.cart
  }

  return {
    cart: cart,
    user: user,
    activeVehicle,
    order
  }
}

export default connect(mapStateToProps, { updateCart })(SearchServices)
